import { useEffect, useState } from "react";
import { Download, Copy, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { fetchAppDownload, fetchEngines, trackDownloadClick } from "@/lib/api";
import { useTitle } from "@/lib/brand";
import { Facts, PageHead } from "@/components/Kit";
import Mark from "@/components/Mark";

/**
 * DOWNLOAD — rebuilt 2026-10-27. The Engines still come only from /api/engines (the same
 * engines.json the app reads — Dog's parity rule, 2026-09-07: a person who sees an Engine can
 * get it); the app's facts from /api/app/download.
 */
export default function AppDownloadPage() {
  useTitle("Download");
  const [meta, setMeta] = useState(null);
  const [engines, setEngines] = useState([]);
  useEffect(() => { fetchAppDownload().then(setMeta).catch(() => {}); }, []);
  useEffect(() => { fetchEngines().then((d) => setEngines(d?.engines || [])).catch(() => {}); }, []);

  const sha = meta?.sha256 && meta.sha256 !== "unknown" ? meta.sha256 : null;
  const copySha = async () => { try { await navigator.clipboard.writeText(sha); toast.success("Checksum copied"); } catch (_) { /* no clipboard */ } };
  const steps = [
    ["Download", "Tap Download for Android on this phone. The app is a single file."],
    ["Allow the install", "Android asks once to allow installs from your browser. Allow it for this install."],
    ["Open and install", "Open the downloaded file and tap Install."],
    ["Get your Engine", "Open Experts. It picks the largest Engine your phone can run and downloads it once — use Wi-Fi."],
    ["Add an Expert", "Choose a free Expert, or open one someone sent you. Then turn the network off — it keeps working."],
  ];

  return (
    <main data-testid="app-download-page" className="ex-page">
      <div className="ex-wrap pb-28">
        <div className="grid gap-12 lg:grid-cols-[1.2fr_1fr] items-center">
          <PageHead eyebrow="Download" title="Experts for Android." lede="Free. No account, no sign-in, no tracking. The app is small; the Engine downloads once, inside it.">
            <div className="flex flex-wrap items-center gap-3">
              {meta?.apk_url ? (
                <a href={meta.apk_url} download="Experts.apk" onClick={() => trackDownloadClick("apk")} className="ex-btn ex-btn-primary" data-testid="apk-download-btn">
                  <Download className="h-4 w-4" strokeWidth={2} /> Download for Android{meta?.apk_size_mb ? ` · ${meta.apk_size_mb} MB` : ""}
                </a>
              ) : (
                <span className="ex-btn ex-btn-primary" aria-disabled="true">Download available shortly</span>
              )}
            </div>
          </PageHead>
          <div className="hidden lg:flex justify-center"><Mark size={300} draw strokeBoost={1.5} className="text-white" /></div>
        </div>

        <section className="grid gap-12 lg:grid-cols-2 pt-6">
          <div>
            <div className="ex-eyebrow mb-5">Install in five steps</div>
            <ol className="space-y-3">
              {steps.map(([h, b], i) => (
                <li key={h} className="ex-card p-5 flex gap-4">
                  <span className="ex-num text-[22px] text-white w-8 flex-none leading-none mt-0.5">{i + 1}</span>
                  <span>
                    <span className="block text-white font-medium">{h}</span>
                    <span className="block ex-small mt-1">{b}</span>
                  </span>
                </li>
              ))}
            </ol>
          </div>
          <div className="space-y-8">
            <div>
              <div className="ex-eyebrow mb-5">About this download</div>
              <Facts rows={[
                ["Version", meta?.version],
                ["App size", meta?.apk_size_mb ? `${meta.apk_size_mb} MB` : null],
                ["Android", meta?.min_android ? `${String(meta.min_android).replace(/^Android\s*/i, "")} or newer` : null],
                ["Publisher", "IP Experts LLC"],
                sha ? ["SHA-256", (
                  <span key="s" className="flex items-start gap-2">
                    <span className="ex-mono break-all text-[color:var(--ex-text2)]">{sha}</span>
                    <button type="button" onClick={copySha} aria-label="Copy checksum" className="text-[color:var(--ex-secondary)] hover:text-white flex-none"><Copy className="h-4 w-4" strokeWidth={1.8} /></button>
                  </span>
                )] : null,
              ].filter((r) => r && r[1])} />
              <p className="ex-tiny mt-3 flex items-start gap-2"><ShieldCheck className="h-4 w-4 flex-none" strokeWidth={1.8} /> Compare the checksum with your download to confirm the file is exactly the one we published.</p>
            </div>
            {engines.length > 0 && (
              <div>
                <div className="ex-eyebrow mb-5">Engines — chosen for your phone in the app</div>
                <div className="space-y-3">
                  {engines.map((e) => (
                    <div key={e.engine_id} className="ex-card p-5">
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-white font-medium">{e.display}</span>
                        {e.size_gb && <span className="ex-small">{e.size_gb} GB</span>}
                      </div>
                      {e.blurb && <div className="ex-small mt-1">{e.blurb}</div>}
                      <div className="ex-tiny mt-3">
                        {[e.ram_tier_gb && `${e.ram_tier_gb} GB of memory or more`, e.free_storage_gb && `${e.free_storage_gb} GB free space`, "Wi-Fi for the one-time download"].filter(Boolean).join(" · ")}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </main>
  );
}
